import { createFileRoute, useRouteContext } from '@tanstack/react-router'
import { useState, useEffect, Fragment } from 'react'
import Tooltip from '../components/Tooltip'

export const Route = createFileRoute('/_layout/opportunities')({
  component: OpportunitiesComponent,
})

function OpportunitiesComponent() {
  const [opportunities, setOpportunities] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [opportunitiesPage, setOpportunitiesPage] = useState(1)
  const [hasMoreOpportunities, setHasMoreOpportunities] = useState(true)
  const [expandedRows, setExpandedRows] = useState(new Set<string>())
  
  const [opportunityFilters, setOpportunityFilters] = useState({
    minSpread: 0.01, // 1%
    minVolume: 0,
    minSimilarity: 0.75,
    platform: '',
    total: 0,
    avgSpread: 0
  })

  useEffect(() => {
    loadOpportunities(1)
  }, [opportunityFilters.minSpread, opportunityFilters.minVolume, opportunityFilters.minSimilarity, opportunityFilters.platform])

  const loadOpportunities = async (page: number) => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        minSpread: opportunityFilters.minSpread.toString(),
        minVolume: opportunityFilters.minVolume.toString(),
        minSimilarity: opportunityFilters.minSimilarity.toString()
      })
      
      if (opportunityFilters.platform) {
        params.append('platform1', opportunityFilters.platform)
      }
      
      const response = await fetch(`https://skybit-api.botnet-599.workers.dev/opportunities?${params}`)
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to fetch opportunities')
      }
      
      const data = await response.json()
      
      setOpportunities(data.opportunities || [])
      setOpportunitiesPage(page)
      setHasMoreOpportunities((data.opportunities || []).length === 50)
      
      setOpportunityFilters(prev => ({
        ...prev,
        total: data.total || 0,
        avgSpread: data.avgSpread || 0
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load opportunities')
    } finally {
      setLoading(false)
    }
  }

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const formatDate = (timestamp?: number | string) => {
    if (!timestamp) return 'N/A'
    
    let date: Date
    if (typeof timestamp === 'string') {
      date = new Date(timestamp)
    } else {
      date = new Date(timestamp * 1000)
    }
    
    if (isNaN(date.getTime())) return 'Invalid Date'
    
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const formatVolume = (volume?: number) => {
    if (!volume) return '$0'
    if (volume >= 1000000) return `$${(volume / 1000000).toFixed(1)}M`
    if (volume >= 1000) return `$${(volume / 1000).toFixed(0)}K`
    return `$${volume.toFixed(0)}`
  }

  const getPlatformPair = (opp: any) => {
    const platforms = new Set<string>()
    if (opp.polymarket_id) platforms.add('polymarket')
    if (opp.kalshi_id) platforms.add('kalshi')
    if (opp.predictit_id) platforms.add('predictit')
    return Array.from(platforms).join(' ↔ ').toUpperCase()
  }

  const getStrategyDescription = (opp: any) => {
    if (opp.arb_type === 'CROSS_MARKET') {
      return 'Cross-Market Arbitrage'
    }
    return opp.arb_type || 'Unknown'
  }

  if (loading && opportunities.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading opportunities...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        color: 'var(--sacred-text-dim)'
      }}>
        <h3 style={{ color: 'var(--sacred-text)', marginBottom: '1rem' }}>
          Opportunities Temporarily Unavailable
        </h3>
        <p style={{ fontSize: '0.7rem', marginBottom: '0.5rem' }}>
          The opportunities service is currently experiencing issues.
        </p>
        <p style={{ fontSize: '0.65rem', color: 'var(--sacred-error)' }}>
          Error: {error}
        </p>
      </div>
    )
  }

  return (
    <>
      <div style={{ 
        padding: '0.75rem 1rem', 
        borderBottom: '1px solid var(--sacred-border)', 
        background: 'var(--sacred-surface)',
        fontSize: '0.65rem',
        fontWeight: 'bold',
        letterSpacing: '0.1em',
        textTransform: 'uppercase'
      }}>
        {opportunityFilters.total ? opportunityFilters.total.toLocaleString() + ' TOTAL' : ''} | {opportunities.length} SHOWING | {((opportunityFilters.avgSpread || 0) * 100).toFixed(1) + '% AVG SPREAD'}
      </div>
      
      {/* Filter Controls */}
      <div className="filter-controls filters-bar">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div>
            <label style={{ fontSize: '0.6rem', color: 'var(--sacred-text-dim)', display: 'block', marginBottom: '0.25rem' }}>MIN SPREAD %</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={opportunityFilters.minSpread * 100}
              onChange={(e) => setOpportunityFilters(prev => ({ ...prev, minSpread: parseFloat(e.target.value) / 100 }))}
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.25rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.6rem', color: 'var(--sacred-text-dim)', display: 'block', marginBottom: '0.25rem' }}>MIN VOLUME $</label>
            <input
              type="number"
              min="0"
              step="100"
              value={opportunityFilters.minVolume}
              onChange={(e) => setOpportunityFilters(prev => ({ ...prev, minVolume: parseFloat(e.target.value) }))}
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.25rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.6rem', color: 'var(--sacred-text-dim)', display: 'block', marginBottom: '0.25rem' }}>MIN SIMILARITY %</label>
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              value={opportunityFilters.minSimilarity * 100}
              onChange={(e) => setOpportunityFilters(prev => ({ ...prev, minSimilarity: parseFloat(e.target.value) / 100 }))}
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.25rem',
                fontSize: '0.65rem'
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: '0.6rem', color: 'var(--sacred-text-dim)', display: 'block', marginBottom: '0.25rem' }}>PLATFORM</label>
            <select
              value={opportunityFilters.platform}
              onChange={(e) => setOpportunityFilters(prev => ({ ...prev, platform: e.target.value }))}
              style={{
                width: '100%',
                background: 'var(--sacred-surface)',
                border: '1px solid var(--sacred-border)',
                color: 'var(--sacred-text)',
                padding: '0.25rem',
                fontSize: '0.65rem'
              }}
            >
              <option value="">All Platforms</option>
              <option value="polymarket">Polymarket</option>
              <option value="kalshi">Kalshi</option>
              <option value="predictit">PredictIt</option>
            </select>
          </div>
        </div>
        <button
          onClick={() => loadOpportunities(1)}
          className="filter-button"
          style={{ marginTop: '0.5rem' }}
        >
          APPLY FILTERS
        </button>
      </div>

      {/* Opportunities Table */}
      <table className="data-table">
        <thead>
          <tr>
            <th>
              <Tooltip content="Type of arbitrage opportunity detected">
                Type
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Trading platforms where arbitrage exists">
                Platforms
              </Tooltip>
            </th>
            <th>
              <Tooltip content="The market question or event">
                Market
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Price difference between platforms">
                Spread
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Estimated profit from $1000 trade">
                Est. Profit
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Combined trading volume across platforms">
                Volume
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Combined liquidity across platforms">
                Liquidity
              </Tooltip>
            </th>
            <th>
              <Tooltip content="Question similarity score (for cross-market)">
                Similarity
              </Tooltip>
            </th>
            <th>
              <Tooltip content="When this opportunity was found">
                Found
              </Tooltip>
            </th>
          </tr>
        </thead>
        <tbody>
          {opportunities.map(opp => (
            <Fragment key={opp.id}>
              <tr 
                className={`market-row ${expandedRows.has(opp.id) ? 'expanded' : ''}`}
                onClick={() => toggleRow(opp.id)}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  <span className="expand-icon">{expandedRows.has(opp.id) ? '▼' : '▶'}</span>
                  <span style={{ 
                    background: 'var(--sacred-blue)',
                    color: 'var(--sacred-bg)',
                    padding: '0.2rem 0.5rem',
                    fontSize: '0.55rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    {opp.arb_type === 'CROSS_MARKET' ? 'CROSS' : 'ARB'}
                  </span>
                </td>
                <td>{getPlatformPair(opp)}</td>
                <td>{opp.market1_question || opp.market_question || 'N/A'}</td>
                <td className="price-yes" style={{ fontWeight: 'bold' }}>
                  {((opp.spread || 0) * 100).toFixed(1)}%
                </td>
                <td className="price-yes">
                  ${((opp.spread || 0) * 1000).toFixed(0)}
                </td>
                <td>{formatVolume(opp.volume)}</td>
                <td>{formatVolume(opp.liquidity)}</td>
                <td>
                  {opp.similarity_score ? `${(opp.similarity_score * 100).toFixed(0)}%` : '-'}
                </td>
                <td>{formatDate(opp.found_at)}</td>
              </tr>
              {expandedRows.has(opp.id) && (
                <tr className="market-details">
                  <td colSpan={9}>
                    <div className="details-content">
                      <div className="detail-section">
                        <h4>Opportunity Details</h4>
                        <div className="detail-row">
                          <span className="detail-label">Strategy:</span>
                          <span className="detail-value">{getStrategyDescription(opp)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Market 1:</span>
                          <span className="detail-value">{opp.market1_question || 'N/A'}</span>
                        </div>
                        {opp.market2_question && (
                          <div className="detail-row">
                            <span className="detail-label">Market 2:</span>
                            <span className="detail-value">{opp.market2_question}</span>
                          </div>
                        )}
                        <div className="detail-row">
                          <span className="detail-label">Found At:</span>
                          <span className="detail-value">
                            {new Date(opp.found_at * 1000).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      
                      <div className="detail-section">
                        <h4>Price Comparison</h4>
                        <div style={{ display: 'grid', gap: '0.5rem' }}>
                          {opp.polymarket_yes !== undefined && (
                            <div className="detail-row">
                              <span className="detail-label">Polymarket:</span>
                              <span className="detail-value">
                                YES: ${(opp.polymarket_yes || 0).toFixed(2)} | 
                                NO: ${(opp.polymarket_no || 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {opp.kalshi_yes !== undefined && (
                            <div className="detail-row">
                              <span className="detail-label">Kalshi:</span>
                              <span className="detail-value">
                                YES: ${(opp.kalshi_yes || 0).toFixed(2)} | 
                                NO: ${(opp.kalshi_no || 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {opp.predictit_yes !== undefined && (
                            <div className="detail-row">
                              <span className="detail-label">PredictIt:</span>
                              <span className="detail-value">
                                YES: ${(opp.predictit_yes || 0).toFixed(2)} | 
                                NO: ${(opp.predictit_no || 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="action-buttons">
                          <button className="action-btn">
                            ANALYZE
                          </button>
                          <button className="action-btn primary">
                            EXECUTE TRADE
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="pagination-controls">
        <button 
          onClick={() => {
            setOpportunitiesPage(prev => prev - 1)
            loadOpportunities(opportunitiesPage - 1)
          }}
          disabled={opportunitiesPage === 1 || loading}
          className="control-btn"
        >
          PREV
        </button>
        
        <span className="pagination-info">
          PAGE {opportunitiesPage}
        </span>
        
        <button 
          onClick={() => {
            setOpportunitiesPage(prev => prev + 1)
            loadOpportunities(opportunitiesPage + 1)
          }}
          disabled={!hasMoreOpportunities || loading}
          className="control-btn"
        >
          NEXT
        </button>
      </div>

      {opportunities.length === 0 && !loading && (
        <div className="empty-state">
          <h3>No opportunities found</h3>
          <p>Try adjusting your filters or check back later</p>
        </div>
      )}
    </>
  )
}